import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import vlog from '../../javascript/vlog';

// V1-COMPAT: generalizes my_inbox/withV2ProfileSlug.jsx's V2 identity lookup for hosts that
// don't require identity to render at all (unlike the inbox, which has nothing to show without
// it). Wraps a copied V2 component so it can read globalThis.Qwoted.CURRENT_USER_ID /
// CURRENT_PROFILE_ID / CURRENT_SLUG / CURRENT_PROFILE_TYPE the way it does when V2 hosts itself.
//
// Children mount only once this resolver OWNS the globals for the current `userId` -- an
// ownership gate, not a profile-lookup gate. The first render (mount, or any render right after
// `userId` changes) returns null while `clearedFor` (a piece of state) doesn't match `userId`
// yet. The mount/userId-change effect clears the four globals synchronously and THEN flips
// `clearedFor` to the current `userId`, which is what actually opens the gate on the next
// render. That ordering is the whole point: React 18 runs a mounted child's own render and
// mount-effects before its parent's passive effect runs, so if children mounted on the SAME
// render that kicks off the clear, a child's mount effect (e.g. useSavedGroups/useLastMessages
// reading globalThis.Qwoted.CURRENT_PROFILE_ID to fire an authenticated request) could still
// observe a previous page's or viewer's stale identity for one frame. Gating the mount itself
// on a state flag set only after the clear has actually run closes that window -- nothing in
// the wrapped subtree exists yet to observe anything. The gate never waits on the network, only
// on the clear, so this is still "immediate" in the sense that matters: no spinner, no CTA, no
// blocking on the profile lookup below.
//
// A signed-in viewer (a `userId` prop, the V1 user id) resolves the V2 profile by
// user_migrate_id in the background, with retries, once the gate above has already opened.
// A resolved profile writes the four globals and bumps `identityEpoch`, which is used as the
// wrapped component's `key` so a resolution landing after children have already mounted forces
// a remount, letting identity-gated features that already rendered (and saw no identity) re-init
// against the now-present globals. A definitive failure (a real error, not a transient one)
// leaves the globals cleared and logs a warning -- the page just stays in the same
// logged-out-shaped degraded mode an anonymous viewer always sees. Anonymous viewer (no
// `userId`): nothing to resolve, globals stay cleared, no remount ever happens. A `userId`
// change resets both `clearedFor` and `identityEpoch`, so a viewer swap can never render even
// one frame under the previous viewer's identity. Every consumer in the copied trees
// (SavedGroupsContext, LastMessagesContext) already reads these globals defensively
// (`globalThis.Qwoted?.CURRENT_USER_ID`) and degrades to its logged-out behavior when they're
// absent, so rendering with them cleared is always a safe default.
//
// my_inbox stays on withV2ProfileSlug -- it blocks rendering on a NO_PROFILE/ERROR state with
// its own full-page CTA, which fits a page that IS the identity-gated feature. This host is for
// pages (like the source directory) that are useful without identity, so a failed or still-
// pending lookup here leaves the globals cleared and renders children anyway, rather than
// blocking the whole page behind a spinner or an onboarding CTA. That's the DEFAULT
// (`blockUntilSettled: false`) behavior, and the source directory keeps it.
//
// The four V2-in-V1 profile SHOW pages (with_v2_entity.jsx's hosts) pass `blockUntilSettled:
// true` instead. Those pages already block on their OWN entity resolution (with_v2_entity.jsx
// renders nothing until it resolves), so there is no "immediate, no-spinner" render to protect --
// unlike the directory, nothing is on screen yet either way. Without blocking here too, the
// entity HOC could resolve (fast) and mount the wrapped tree BEFORE identity has resolved (slow,
// or still retrying), reading `globalThis.Qwoted.CURRENT_USER_ID` as absent even though this
// controller's own `viewerContext.isAuthenticated` already says true -- a signed-in viewer would
// see a flash of the anonymous "Claim this page" CTA before a remount corrects it. When
// `blockUntilSettled` is set and `userId` is present, this HOC withholds the wrapped subtree
// (renders null, same as the ownership gate above) until identity resolution SETTLES -- a
// successful write (mount as authenticated) or a definitive failure (globals stay cleared, mount
// anonymous-degraded, same as the non-blocking path's failure case) -- so the copied tree only
// ever mounts once, already consistent with `viewerContext`. An anonymous viewer (no `userId`)
// is never blocked by this flag: there is nothing to wait for, so behavior is identical to the
// non-blocking default.
//
// `blockUntilSettled` on its own only fixes the MOUNT timing, not a contradiction still possible
// after settling: the server rendered `viewerContext` (and hence `editable`/`isAuthenticated`/
// `role`/`profileEditable`) from V1's own session before this HOC ever ran, so a signed-in
// viewer whose V2 identity resolution failed or exhausted its retries would otherwise mount with
// `viewerContext.isAuthenticated: true` (and maybe `editable: true`) sitting right next to
// cleared identity globals -- e.g. the company sidebar decides whether to show "Claim this page"
// off `userId` (absent) while other controls on the same page trust `viewerContext.editable`
// (still true), so the page contradicts itself. The four show hosts also pass
// `normalizeViewerContextOnFailure: true` to close that gap: once resolution has settled WITHOUT
// identity for a signed-in viewer, the `viewerContext` prop handed to the wrapped tree is
// rewritten to the fully anonymous shape (`editable`/`isAuthenticated`/`planPaid`: false, `role`:
// null, and `profileEditable`: false if the original carried that key at all) rather than passed
// through as-is. Without V2 identity the page cannot perform any member action regardless of what
// V1 says, so a coherent anonymous page beats a contradictory authenticated-looking one. The
// success path is untouched -- `viewerContext` passes through unchanged once identity actually
// resolves. The source directory passes neither flag: it never carries a `viewerContext` prop at
// all, so there is nothing for this to normalize there.
const PGRST_NO_ROWS_CODE = 'PGRST116';
const RETRY_DELAYS_MS = [1000, 3000, 6000];
const IDENTITY_GLOBAL_KEYS = ['CURRENT_USER_ID', 'CURRENT_PROFILE_ID', 'CURRENT_SLUG', 'CURRENT_PROFILE_TYPE'];
// Sentinel `clearedFor` value distinct from any real `userId` (including `undefined`, the
// anonymous case), so the ownership gate starts closed on every mount and can't accidentally
// read as "already cleared for this viewer" before the effect has ever run.
const UNCLEARED = Symbol('uncleared');

// See withV2ProfileSlug.jsx for the full rationale: a backgrounded/discarded tab's in-flight
// fetch throws a native abort/network error that self-heals on remount and is not worth
// reporting.
const isTransientFetchError = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError') return true;
  const message = typeof error.message === 'string' ? error.message : '';
  return /failed to fetch|load failed|network ?error|aborterror|the (operation|user) (was )?aborted/i.test(message);
};

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Deletes (not just blanks) the four globals so a defensive `globalThis.Qwoted?.CURRENT_USER_ID`
// read sees "absent", the same as it would for a viewer this HOC never resolved at all.
const clearIdentityGlobals = () => {
  if (!globalThis.Qwoted) return;
  IDENTITY_GLOBAL_KEYS.forEach((key) => { delete globalThis.Qwoted[key]; });
};

// V1-COMPAT: rewrites a server-rendered `viewerContext` to the fully anonymous shape -- see the
// `normalizeViewerContextOnFailure` module comment. `profileEditable` is only ever added if the
// original carried it (experts-only; the other three show pages never declare it and must not
// start declaring it here).
const degradeViewerContext = (viewerContext) => {
  if (!viewerContext) return viewerContext;

  const degraded = {
    ...viewerContext,
    editable: false,
    isSelf: false,
    isAuthenticated: false,
    planPaid: false,
    role: null,
  };
  if ('profileEditable' in viewerContext) {
    degraded.profileEditable = false;
  }
  return degraded;
};

function withV2Identity(WrappedComponent, {
  blockUntilSettled = false,
  normalizeViewerContextOnFailure = false,
} = {}) {
  function V2IdentityResolver(props) {
    const { userId, viewerContext } = props;
    // Bumped on a successful resolution; used as WrappedComponent's `key` below so a resolution
    // that lands after children have already rendered forces a remount instead of relying on
    // every identity-gated feature to notice a prop-less global changing under it.
    const [identityEpoch, setIdentityEpoch] = useState(0);
    // The ownership gate: the wrapped subtree is allowed to exist at all only once this equals
    // the current `userId` -- see the module comment for why the gate has to be a render-time
    // check rather than something the effect can enforce after the fact.
    const [clearedFor, setClearedFor] = useState(UNCLEARED);
    // Only consulted when `blockUntilSettled` is on: true once this resolution has reached a
    // terminal outcome for the CURRENT `userId` -- a successful write, a definitive failure, or
    // (so a blocked page can never wait forever) retries exhausted with neither. Reset to false
    // alongside `clearedFor`/`identityEpoch` on every mount and every `userId` change.
    const [settled, setSettled] = useState(false);
    // True only once a resolution has actually WRITTEN the identity globals for the current
    // `userId` (the same condition that bumps `identityEpoch`) -- distinct from `settled`, which
    // also goes true on failure/exhaustion. Consulted by `normalizeViewerContextOnFailure`: a
    // settled-but-not-resolved viewer is exactly the "signed in per V1, anonymous per V2" case
    // that needs its `viewerContext` rewritten. Reset to false alongside the other three.
    const [identityResolved, setIdentityResolved] = useState(false);

    useEffect(() => {
      let cancelled = false;

      // V1-COMPAT: clear first, always, before anything else -- including before deciding
      // whether there's even a userId to resolve. A previous page's (or previous viewer's)
      // resolved identity must never survive into this mount, even for the time it'd take a
      // slow or failing lookup to run. Only AFTER the clear has actually happened does the gate
      // open (via setClearedFor) for this userId -- that's what the render below is keyed on.
      clearIdentityGlobals();
      setIdentityEpoch(0);
      setClearedFor(userId);
      setSettled(false);
      setIdentityResolved(false);

      if (!userId) {
        return () => {
          cancelled = true;
          clearIdentityGlobals();
        };
      }

      const queryProfile = () => globalThis.Qwoted.supabaseClient
        .from('user_profiles')
        .select('user_id, profile_id, slug, type_of_profile')
        .eq('user_migrate_id', userId)
        .single();

      const resolveIdentity = async () => {
        try {
          // Init is async and fired fire-and-forget from the layout. Await it so we don't race
          // ahead of `window.supabaseClient` being set; idempotent.
          if (window.initializeSupabaseClient) {
            await window.initializeSupabaseClient();
          }

          // Retry both PGRST116 ("no row" -- a brand-new signup's V2 profile can take a few
          // seconds to surface) and a transient fetch abort, which usually self-heals on retry.
          // Sequential awaits are intentional: each retry must wait for the previous response.
          /* eslint-disable no-await-in-loop */
          let result = await queryProfile();
          let attempt = 0;
          while (
            !cancelled
            && attempt < RETRY_DELAYS_MS.length
            && (result.error?.code === PGRST_NO_ROWS_CODE || (!result.error && !result.data?.slug)
              || result.status === 0 || isTransientFetchError(result.error))
          ) {
            await sleep(RETRY_DELAYS_MS[attempt]);
            if (cancelled) return;
            result = await queryProfile();
            attempt += 1;
          }
          /* eslint-enable no-await-in-loop */
          if (cancelled) return;

          const { data, error } = result;

          // A resolved profile wires the identity globals and remounts the wrapped subtree.
          // Anything else (no row after retries, a real error) leaves the globals cleared --
          // there is no full-page CTA here, the page just stays in the same degraded shape an
          // anonymous viewer always renders, which is the point of using this HOC instead of
          // withV2ProfileSlug.
          if (!error && data?.slug) {
            globalThis.Qwoted.CURRENT_USER_ID = data.user_id;
            globalThis.Qwoted.CURRENT_PROFILE_ID = data.profile_id;
            globalThis.Qwoted.CURRENT_SLUG = data.slug;
            globalThis.Qwoted.CURRENT_PROFILE_TYPE = data.type_of_profile;
            setIdentityEpoch((epoch) => epoch + 1);
            setIdentityResolved(true);
          } else if (error && !isTransientFetchError(error)) {
            vlog.error('Failed to resolve V2 identity', { userId, error });
            // eslint-disable-next-line no-console -- deliberately survives vlog.disableAll() in production
            console.warn('[V1-COMPAT] Failed to resolve V2 identity; staying in anonymous-degraded mode.', { userId, error });
            window.Rollbar?.error('V2 identity resolution failed', { userId, error });
          }
          // V1-COMPAT: settle regardless of which branch above ran -- including the case where
          // retries exhausted without either a success or a logged definitive failure (a
          // persistent no-row or transient response). `blockUntilSettled` callers must never
          // wait forever, so any terminal outcome of this resolveIdentity() call -- happy or
          // not -- opens their gate.
          if (!cancelled) setSettled(true);
        } catch (err) {
          if (!cancelled) {
            if (!isTransientFetchError(err)) {
              vlog.error('Failed to resolve V2 identity', err);
              // eslint-disable-next-line no-console -- deliberately survives vlog.disableAll() in production
              console.warn('[V1-COMPAT] Failed to resolve V2 identity; staying in anonymous-degraded mode.', err);
              window.Rollbar?.error('V2 identity resolution failed', err);
            }
            setSettled(true);
          }
        }
      };

      resolveIdentity();
      return () => {
        cancelled = true;
        // V1-COMPAT: clear on the way out too -- a signed-in viewer navigating to a page this
        // HOC doesn't wrap (or a userId change) must not leave their identity behind for
        // whatever mounts next.
        clearIdentityGlobals();
      };
    }, [userId]);

    // Ownership gate: nothing mounts until `clearedFor` matches the CURRENT `userId`, which
    // only happens once the effect above has actually cleared the globals for it. On the very
    // first render (mount, or the render right after `userId` changes) this is always false --
    // `clearedFor` still holds either UNCLEARED or the previous `userId`, neither of which can
    // equal the new one -- so this returns null for exactly one frame, never blocking on the
    // background profile lookup that follows.
    if (clearedFor !== userId) return null;

    // Opt-in blocking gate (see the module comment on `blockUntilSettled`): a signed-in viewer's
    // wrapped subtree stays withheld until resolution has settled. Anonymous viewers (no
    // `userId`) and the default non-blocking hosts are never affected -- this is only true when
    // there is a pending resolution actually worth waiting for.
    if (blockUntilSettled && userId && !settled) return null;

    // Opt-in viewerContext normalization (see the module comment on
    // `normalizeViewerContextOnFailure`): once a signed-in viewer's resolution has settled
    // WITHOUT ever writing the identity globals, the server-rendered `viewerContext` no longer
    // describes reality -- rewrite it to the anonymous shape rather than hand the wrapped tree a
    // `viewerContext` that contradicts the cleared globals it also reads. The success path
    // (`identityResolved`) and the default (flag off) both pass `props` through untouched.
    const settledWithoutIdentity = userId && settled && !identityResolved;
    const effectiveProps = (normalizeViewerContextOnFailure && settledWithoutIdentity)
      ? { ...props, viewerContext: degradeViewerContext(viewerContext) }
      : props;

    // `key` only changes when a resolution actually succeeds, so this never remounts on the
    // (frequent) no-op re-renders a retry loop's own state changes would otherwise cause.
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <WrappedComponent key={identityEpoch} {...effectiveProps} />;
  }

  V2IdentityResolver.propTypes = {
    userId: PropTypes.number,
    // eslint-disable-next-line react/forbid-prop-types -- shape varies per host (experts alone
    // add profileEditable); degradeViewerContext() only needs to spread/override known keys, not
    // validate the rest.
    viewerContext: PropTypes.object,
  };

  V2IdentityResolver.defaultProps = {
    userId: undefined,
    viewerContext: undefined,
  };

  return V2IdentityResolver;
}

export default withV2Identity;
